import { useMemo } from "react";
import { MantineProvider, ColorSchemeScript } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import "@fontsource/montserrat/latin-600.css";
import "@fontsource/montserrat/latin-700.css";
import "@fontsource/montserrat/latin-800.css";
import "@fontsource/source-sans-3/latin-400.css";
import "@fontsource/source-sans-3/latin-500.css";
import "@fontsource/source-sans-3/latin-600.css";
import "@fontsource/source-sans-3/latin-700.css";
import { createEvolysTheme } from "../theme/evolysTheme";
import { BrandThemeProvider, useBrandTheme } from "../theme/BrandThemeContext";
import { QueryProvider } from "../providers/QueryProvider";
import { initializeFrontendObservability } from "../observability/sentry";
import { ErrorBoundary } from "./ErrorBoundary";
import "../styles/evolys-shell.css";

interface EvolysProviderProps {
  children: React.ReactNode;
  forceColorScheme?: "light" | "dark";
}

function ThemedEvolysProvider({
  children,
  forceColorScheme,
}: EvolysProviderProps) {
  const { paletteKey } = useBrandTheme();
  const theme = useMemo(() => createEvolysTheme(paletteKey), [paletteKey]);

  return (
    <MantineProvider
      theme={theme}
      defaultColorScheme="auto"
      forceColorScheme={forceColorScheme}
    >
      <DatesProvider settings={{ locale: "en" }}>
        <ErrorBoundary>
          <QueryProvider>{children}</QueryProvider>
        </ErrorBoundary>
      </DatesProvider>
    </MantineProvider>
  );
}

export function EvolysProvider({
  children,
  forceColorScheme,
}: EvolysProviderProps) {
  initializeFrontendObservability();

  return (
    <BrandThemeProvider>
      <ThemedEvolysProvider forceColorScheme={forceColorScheme}>
        {children}
      </ThemedEvolysProvider>
    </BrandThemeProvider>
  );
}

export { ColorSchemeScript };
